import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';

import type { SolutionBuildResponse, SolutionPlan, SolutionPlanResponse } from '../types';

const extractErrorMessage = async (response: Response): Promise<string | undefined> => {
	try {
		const data = (await response.json()) as { message?: unknown };
		if (typeof data?.message === 'string' && data.message.trim()) {
			return data.message;
		}
	} catch {
		// Ignore parse errors and fall back to generic message.
	}

	return undefined;
};

const mapToSolutionPlan = (response: SolutionPlanResponse): SolutionPlan => ({
	...response,
	builds: response.builds.sequence
		.map((build) => ({
			...build,
			solutionId: response.id,
			solutionVersion: response.solutionVersion,
			conversationId: response.conversationId,
		}))
		.sort((a, b) => a.number - b.number),
});

export const fetchSolutionPlan = async (
	workspaceId: string,
	solutionId: string,
	solutionVersion: string,
): Promise<SolutionPlan> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/solution-architect/solution/${solutionId}/version/${solutionVersion}`,
	);

	if (!response.ok) {
		throw new Error('Failed to fetch solution version');
	}
	return mapToSolutionPlan(await response.json());
};

export const postSolutionBuild = async (
	workspaceId: string,
	solutionId: string,
	solutionVersion: string,
	buildNumber: number,
): Promise<SolutionBuildResponse> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/solution-architect/solution/${solutionId}/version/${solutionVersion}/build/${buildNumber}`,
		{
			method: 'POST',
		},
	);
	if (!response.ok) {
		const errorMessage = await extractErrorMessage(response);
		throw new ApiError('Failed to post solution build', {
			statusCode: response.status,
			rawErrorMessage: errorMessage,
		});
	}
	return response.json() as Promise<SolutionBuildResponse>;
};
